import React, { useState, useEffect } from 'react';
import apiService from '../../services/api';
import {
  Box,
  Typography,
  Button,
  Paper,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Switch,
  TablePagination,
  Divider,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Alert,
  AlertTitle,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  CircularProgress
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ContentCopy as DuplicateIcon,
  Save as SaveIcon,
  Cancel as CancelIcon
} from '@mui/icons-material';

function ModelConfiguration() {
  const [platform, setPlatform] = useState('openai');
  const [configName, setConfigName] = useState('');
  const [configFields, setConfigFields] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [isApplying, setIsApplying] = useState(false);
  const [applyingConfigId, setApplyingConfigId] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [modelConfigurations, setModelConfigurations] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [editingConfigId, setEditingConfigId] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [currentModel, setCurrentModel] = useState('');
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [deleteConfigId, setDeleteConfigId] = useState(null);

  useEffect(() => {
    // Fetch existing configurations when component mounts
    fetchModelConfigurations();
    fetchCurrentModel();
  }, []);

  const fetchCurrentModel = async () => {
    try {
      const response = await apiService.getAvailableModels();
      setCurrentModel(response.current_model);
    } catch (error) {
      console.error('Error fetching current model:', error);
    }
  };

  const fetchModelConfigurations = async () => {
    try {
      const response = await apiService.getModelConfigurations();
      setModelConfigurations(response.configurations || []);
    } catch (error) {
      console.error('Error fetching model configurations:', error);
      setErrorMessage('Failed to load model configurations');
    }
  };

  const resetForm = () => {
    setConfigName('');
    setConfigFields({});
    setIsEditing(false);
    setEditingConfigId(null);
    setErrorMessage('');
    setSuccessMessage('');
  };

  const loadPlatformDefaults = async (selectedPlatform) => {
    setPlatform(selectedPlatform);
    setErrorMessage('');
    setSuccessMessage('');
    
    try {
      // Fetch current configuration for the selected platform
      const config = await apiService.getModelConfiguration(selectedPlatform);
      
      let fields = {};
      
      switch (selectedPlatform) {
        case 'openai':
          fields = {
            model_name: config.config?.model_name || '',
            openai_api_key: config.config?.api_key || '',
          };
          break;
        case 'azure_openai':
          fields = {
            openai_api_type: 'azure',
            openai_api_key: config.config?.api_key || '',
            openai_api_base: config.config?.endpoint || '',
            openai_api_version: config.config?.api_version || '',
            deployment_name: config.config?.deployment_name || '',
            model_name: config.config?.model_name || '',
          };
          break;
        case 'gemini':
          fields = {
            google_api_key: config.config?.api_key || '',
            model_name: config.config?.model_name || '',
            temperature: config.config?.temperature || '0.7',
            top_p: config.config?.top_p || '0.95',
          };
          break;
        default:
          fields = {};
      }
      
      setConfigFields(fields);
    } catch (error) {
      console.error('Error fetching model configuration:', error);
      setErrorMessage('Failed to load platform defaults');
    }
  };

  const handleAddNew = () => {
    resetForm();
    loadPlatformDefaults(platform);
  };

  const handlePlatformChange = (e) => {
    loadPlatformDefaults(e.target.value);
  };

  const handleConfigNameChange = (e) => {
    setConfigName(e.target.value);
  };

  const handleFieldChange = (field, value) => {
    setConfigFields({
      ...configFields,
      [field]: value
    });
  };

  const handleEdit = (config) => {
    setIsEditing(true);
    setEditingConfigId(config.id);
    setPlatform(config.platform);
    setConfigName(config.config_name);
    
    // Set fields based on platform
    let fields = {};
    switch (config.platform) {
      case 'openai':
        fields = {
          model_name: config.model_name || '',
          openai_api_key: config.openai_api_key || '',
        };
        break;
      case 'azure_openai':
        fields = {
          openai_api_type: config.openai_api_type || 'azure',
          openai_api_key: config.openai_api_key || '',
          openai_api_base: config.openai_api_base || '',
          openai_api_version: config.openai_api_version || '',
          deployment_name: config.deployment_name || '',
          model_name: config.model_name || '',
        };
        break;
      case 'gemini':
        fields = {
          google_api_key: config.google_api_key || '',
          model_name: config.model_name || '',
          temperature: config.temperature || '',
          top_p: config.top_p || '',
        };
        break;
      default:
        fields = {};
    }
    
    setConfigFields(fields);
  };

  const handleApply = async (configId) => {
    setIsApplying(true);
    setApplyingConfigId(configId);
    setErrorMessage('');
    setSuccessMessage('');
    
    try {
      const response = await apiService.applyModelConfiguration(configId);
      
      if (response.warning) {
        setSuccessMessage(response.message);
      } else {
        setSuccessMessage(`Configuration applied successfully! The model has been switched to ${response.platform}.`);
        
        // Update the current model
        setCurrentModel(response.platform);
      }
      
      // Update configurations list to reflect any changes
      await fetchModelConfigurations();
    } catch (error) {
      console.error('Error applying configuration:', error);
      setErrorMessage('Failed to apply configuration. Please check the server logs for details.');
    } finally {
      setIsApplying(false);
      setApplyingConfigId(null);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfigId) return;
    
    try {
      await apiService.deleteModelConfiguration(deleteConfigId);
      await fetchModelConfigurations();
      setSuccessMessage('Configuration deleted successfully');
    } catch (error) {
      console.error('Error deleting configuration:', error);
      setErrorMessage('Failed to delete configuration');
    } finally {
      setOpenDeleteDialog(false);
      setDeleteConfigId(null);
    }
  };

  const handleDeleteClick = (configId) => {
    setDeleteConfigId(configId);
    setOpenDeleteDialog(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!configName.trim()) {
      setErrorMessage('Configuration name is required');
      return;
    }
    
    setIsSaving(true);
    setErrorMessage('');
    setSuccessMessage('');

    try {
      // Create the configuration object to send to the backend
      const configData = {
        id: isEditing ? editingConfigId : null,
        config_name: configName,
        platform,
        ...configFields
      };
      
      // Call the API to save the configuration
      if (isEditing) {
        await apiService.updateModelConfiguration(configData);
      } else {
        await apiService.saveModelConfiguration(configData);
      }
      
      // Update the list of configurations
      await fetchModelConfigurations();
      
      setSuccessMessage(`Configuration ${isEditing ? 'updated' : 'saved'} successfully!`);
      
      // Reset form after successful save
      resetForm();
    } catch (error) {
      console.error('Error saving configuration:', error);
      setErrorMessage('Failed to save configuration. Please check your inputs and try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDuplicate = (config) => {
    setIsEditing(false);
    setEditingConfigId(null);
    setPlatform(config.platform);
    setConfigName(`${config.config_name} (Copy)`);
    
    // Copy all fields except ID
    const fieldsToCopy = { ...config };
    delete fieldsToCopy.id;
    delete fieldsToCopy.config_name;
    delete fieldsToCopy.platform;
    delete fieldsToCopy.created_at;
    delete fieldsToCopy.updated_at;
    delete fieldsToCopy.is_default;
    
    setConfigFields(fieldsToCopy);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const renderFields = () => {
    switch (platform) {
      case 'openai':
        return (
          <>
            <TextField
              fullWidth
              margin="normal"
              id="model_name"
              label="Model Name"
              variant="outlined"
              value={configFields.model_name || ''}
              onChange={(e) => handleFieldChange('model_name', e.target.value)}
              placeholder="e.g., gpt-3.5-turbo"
            />
            <TextField
              fullWidth
              margin="normal"
              id="openai_api_key"
              label="API Key"
              variant="outlined"
              type="password"
              value={configFields.openai_api_key || ''}
              onChange={(e) => handleFieldChange('openai_api_key', e.target.value)}
              placeholder="Enter your OpenAI API key"
            />
          </>
        );
        
      case 'azure_openai':
        return (
          <>
            <TextField
              fullWidth
              margin="normal"
              id="openai_api_base"
              label="Model Endpoint"
              variant="outlined"
              value={configFields.openai_api_base || ''}
              onChange={(e) => handleFieldChange('openai_api_base', e.target.value)}
              placeholder="e.g., https://your-resource.openai.azure.com/"
            />
            <TextField
              fullWidth
              margin="normal"
              id="openai_api_type"
              label="API Type"
              variant="outlined"
              value={configFields.openai_api_type || 'azure'}
              onChange={(e) => handleFieldChange('openai_api_type', e.target.value)}
              InputProps={{
                readOnly: true,
              }}
            />
            <TextField
              fullWidth
              margin="normal"
              id="openai_api_version"
              label="API Version"
              variant="outlined"
              value={configFields.openai_api_version || ''}
              onChange={(e) => handleFieldChange('openai_api_version', e.target.value)}
              placeholder="e.g., 2023-03-15-preview"
            />
            <TextField
              fullWidth
              margin="normal"
              id="deployment_name"
              label="Deployment Name"
              variant="outlined"
              value={configFields.deployment_name || ''}
              onChange={(e) => handleFieldChange('deployment_name', e.target.value)}
              placeholder="Enter your deployment name"
            />
            <TextField
              fullWidth
              margin="normal"
              id="model_name"
              label="Model Name"
              variant="outlined"
              value={configFields.model_name || ''}
              onChange={(e) => handleFieldChange('model_name', e.target.value)}
              placeholder="Enter model name"
            />
            <TextField
              fullWidth
              margin="normal"
              id="openai_api_key"
              label="API Key"
              variant="outlined"
              type="password"
              value={configFields.openai_api_key || ''}
              onChange={(e) => handleFieldChange('openai_api_key', e.target.value)}
              placeholder="Enter your Azure OpenAI API key"
            />
          </>
        );
        
      case 'gemini':
        return (
          <>
            <TextField
              fullWidth
              margin="normal"
              id="model_name"
              label="Model Name"
              variant="outlined"
              value={configFields.model_name || ''}
              onChange={(e) => handleFieldChange('model_name', e.target.value)}
              placeholder="e.g., gemini-1.5-pro"
            />
            <TextField
              fullWidth
              margin="normal"
              id="google_api_key"
              label="API Key"
              variant="outlined"
              type="password"
              value={configFields.google_api_key || ''}
              onChange={(e) => handleFieldChange('google_api_key', e.target.value)}
              placeholder="Enter your Google API key"
            />
            <TextField
              fullWidth
              margin="normal"
              id="temperature"
              label="Temperature"
              variant="outlined"
              value={configFields.temperature || ''}
              onChange={(e) => handleFieldChange('temperature', e.target.value)}
              placeholder="e.g., 0.7"
            />
            <TextField
              fullWidth
              margin="normal"
              id="top_p"
              label="Top-p"
              variant="outlined"
              value={configFields.top_p || ''}
              onChange={(e) => handleFieldChange('top_p', e.target.value)}
              placeholder="e.g., 0.95"
            />
          </>
        );
        
      default:
        return null;
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h4" component="h1">
            Model Configuration
          </Typography>
          <Button 
            variant="contained" 
            color="primary" 
            startIcon={<AddIcon />}
            onClick={handleAddNew}
          >
            Add New Configuration
          </Button>
        </Box>
        
        <Divider sx={{ mb: 3 }} />
        
        {(errorMessage || successMessage) && (
          <Box sx={{ mb: 3 }}>
            {errorMessage && (
              <Alert severity="error" onClose={() => setErrorMessage('')}>
                <AlertTitle>Error</AlertTitle>
                {errorMessage}
              </Alert>
            )}
            {successMessage && (
              <Alert severity="success" onClose={() => setSuccessMessage('')}>
                <AlertTitle>Success</AlertTitle>
                {successMessage}
              </Alert>
            )}
          </Box>
        )}
        
        <Grid container spacing={3}>
          <Grid item xs={12} md={5}>
            <Card>
              <CardHeader 
                title={isEditing ? "Edit Configuration" : "Add New Configuration"}
                action={
                  isEditing && (
                    <Button 
                      color="primary" 
                      onClick={handleAddNew}
                      startIcon={<AddIcon />}
                    >
                      Add New
                    </Button>
                  )
                }
              />
              <CardContent>
                <form onSubmit={handleSubmit}>
                  <TextField
                    fullWidth
                    required
                    margin="normal"
                    id="config_name"
                    label="Configuration Name"
                    variant="outlined"
                    value={configName}
                    onChange={handleConfigNameChange}
                    placeholder="Enter a name for this configuration"
                  />
                  
                  <FormControl fullWidth margin="normal">
                    <InputLabel id="platform-label">Platform</InputLabel>
                    <Select
                      labelId="platform-label"
                      id="platform"
                      value={platform}
                      label="Platform"
                      onChange={handlePlatformChange}
                    >
                      <MenuItem value="openai">OpenAI</MenuItem>
                      <MenuItem value="azure_openai">Azure OpenAI</MenuItem>
                      <MenuItem value="gemini">Google Gemini</MenuItem>
                    </Select>
                  </FormControl>
                  
                  {/* Platform-specific fields */}
                  {renderFields()}
                  
                  <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
                    <Button 
                      variant="contained" 
                      color="primary" 
                      type="submit"
                      disabled={isSaving}
                      startIcon={isSaving ? <CircularProgress size={24} /> : <SaveIcon />}
                    >
                      {isEditing ? "Update" : "Save Configuration"}
                    </Button>
                    {isEditing && (
                      <Button 
                        variant="outlined" 
                        color="secondary" 
                        onClick={resetForm}
                        startIcon={<CancelIcon />}
                      >
                        Cancel
                      </Button>
                    )}
                  </Box>
                </form>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={7}>
            <TableContainer component={Paper}>
              <Table size="medium">
                <TableHead>
                  <TableRow>
                    <TableCell>Configuration Name</TableCell>
                    <TableCell>Platform</TableCell>
                    <TableCell>Model Name</TableCell>
                    <TableCell>Active</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {modelConfigurations.length > 0 ? (
                    modelConfigurations
                      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                      .map((config) => (
                        <TableRow 
                          key={config.id} 
                          sx={{ backgroundColor: config.active ? 'action.selected' : 'inherit' }}
                        >
                          <TableCell>{config.config_name}</TableCell>
                          <TableCell>
                            {config.platform === 'openai' && 'OpenAI'}
                            {config.platform === 'azure_openai' && 'Azure OpenAI'}
                            {config.platform === 'gemini' && 'Google Gemini'}
                          </TableCell>
                          <TableCell>{config.model_name || 'Not set'}</TableCell>
                          <TableCell>
                            <Switch
                              checked={config.active}
                              onChange={() => !config.active && handleApply(config.id)}
                              disabled={isApplying || config.active}
                            />
                            {isApplying && applyingConfigId === config.id && (
                              <CircularProgress size={24} sx={{ ml: 1 }} />
                            )}
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              <IconButton 
                                color="primary" 
                                onClick={() => handleEdit(config)}
                                size="small"
                              >
                                <EditIcon />
                              </IconButton>
                              <IconButton 
                                color="secondary" 
                                onClick={() => handleDuplicate(config)}
                                size="small"
                              >
                                <DuplicateIcon />
                              </IconButton>
                              <IconButton 
                                color="error" 
                                onClick={() => handleDeleteClick(config.id)}
                                size="small"
                                disabled={config.platform === currentModel}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        No configurations available
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
              <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                component="div"
                count={modelConfigurations.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </TableContainer>
          </Grid>
        </Grid>
      </Paper>
      
      {/* Confirm Delete Dialog */}
      <Dialog
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
      >
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this model configuration? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)} color="primary">
            Cancel
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default ModelConfiguration;
